<script lang="ts">
    import Fa from 'svelte-fa';
    import { faStar, faThumbsUp, faThumbsDown } from '@fortawesome/pro-solid-svg-icons';

    import { page as pageData } from '$app/stores';
    import Modal from './Modal.svelte';
    import { goto } from '$app/navigation';

    export let data;

    const baseUrl = `/api/reviews/${data?.product?.handle}`;

    let page = 1;
    let limit = 5;

    let sortingOption = 1;
    const sortOptions = [
        { id: 1, title: 'Most Recent', sortBy: 'created_at', sortDir: 'desc' },
        { id: 2, title: 'Highest Rating', sortBy: 'rating', sortDir: 'desc' },
        { id: 3, title: 'Lowest Rating', sortBy: 'rating', sortDir: 'asc' },
        { id: 4, title: 'Most Helpful', sortBy: 'most_helpful', sortDir: 'desc' }
    ];
    $: sorting = sortOptions.filter((option) => option.id === sortingOption).map((entry) => ({ sortBy: entry.sortBy, sortDir: entry.sortDir }))?.[0];

    // Load streamed reviews but overload with hydrated nav
    let reviews = data.streamed.reviews;

    const fetcher = (url) =>
        new Promise((resolve, reject) => {
            fetch(url).then((response) => {
                if (!response.ok) reject(response.statusText);
                resolve(response.json());
            });
        });

    const nextPage = () => {
        page = page + 1;
        const nextUrl = `${baseUrl}?${new URLSearchParams({ page, limit, ...sorting })}`;
        reviews = fetcher(nextUrl);
        goto('#reviewTop', { replaceState: true });
    };
    const prevPage = () => {
        page = page > 1 ? page - 1 : 1;
        const nextUrl = `${baseUrl}?${new URLSearchParams({ page, limit, ...sorting })}`;
        reviews = fetcher(nextUrl);
        goto('#reviewTop', { replaceState: true });
    };

    let optimisticThumbs = [];
    const thumbs = (reviewId, direction) => {
        // If already triggered for this reviewId do nothing;
        if (optimisticThumbs.filter(({ reviewId: optimisticReviewId }) => optimisticReviewId === reviewId).length) return;
        // Optimistic state
        optimisticThumbs = [...optimisticThumbs, { reviewId, direction }];
        fetch(`https://judge.me/thumbs`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
            },
            body: `review_uuid=${reviewId}&thumb_type=${direction}`
        });
    };

    // open thank you message if user left a review from email campaign
    $: openThanksMessage = $pageData.url.searchParams.has('judgeme_follow_up_token');
</script>

<div id="reviewTop"></div>
{#await reviews}
    <p>Loading reviews</p>
{:then reviewData}
    {#if reviewData?.reviews?.length}
        <div class="mb-4 border-b border-color-text/10 pb-4 lg:grid lg:grid-cols-12 lg:items-start">
            <div class="lg:col-span-4">
                <div class="mb-2">
                    <span>{reviewData?.meta?.average} Stars</span>
                    <span>Based on {reviewData?.meta?.totalReviews} reviews</span>
                </div>
                {#each Object.entries(reviewData?.meta?.starRatings)
                    .slice()
                    .sort((a, b) => b[0] - a[0]) as [index, rating] (rating)}
                    <div class="grid w-full grid-cols-3 items-center justify-start gap-3">
                        <div class="flex gap-1.5 text-left">
                            {#each { length: index } as _, i}
                                <Fa icon={faStar} color="#fbcd0a" size="sm" />
                            {/each}
                            {#each { length: 5 - index } as _, i}
                                <Fa icon={faStar} class="text-base-700" size="sm" />
                            {/each}
                        </div>
                        <div class="block w-full overflow-hidden rounded-xl bg-base-700">
                            <div class="h-2 bg-[#fbcd0a]" style="width:{rating?.percentage}%" />
                        </div>
                        <div class="text-sm italic opacity-80">
                            {rating?.percentage}% ({rating?.reviews})
                        </div>
                    </div>
                {/each}
            </div>
        </div>

        {#each reviewData?.reviews as review, i}
            <div class="mb-4 border-b border-color-text/10 pb-4">
                <div class="mb-2">
                    <div class="flex items-center gap-1.5">
                        {#each { length: review?.rating } as _, i}
                            <Fa icon={faStar} color="#fbcd0a" size="sm" />
                        {/each}
                        {#each { length: 5 - review?.rating } as _, i}
                            <Fa icon={faStar} class="text-base-700" size="sm" />
                        {/each}
                        <span class="text-sm italic opacity-80">{review?.date}</span>
                    </div>
                    <div class="block text-sm italic opacity-80">
                        {review?.name} <span>- Verified</span>
                    </div>
                </div>

                <div class="lg:max-w-4xl">
                    <h3 class="font-semibold dark:text-purple-300">{review?.title}</h3>
                    <p>{review?.body}</p>
                </div>

                <div class="mt-2 flex w-full items-center justify-end gap-6 text-sm text-color-text opacity-80">
                    <button on:click={() => thumbs(review?.uuid, 'up')}>
                        <Fa icon={faThumbsUp} size="sm" class="inline-block" /> ({Number(review?.votes?.up) +
                            optimisticThumbs.filter((thumb) => thumb.reviewId === review?.uuid && thumb.direction === 'up').length})
                    </button>
                    <button on:click={() => thumbs(review?.uuid, 'down')}>
                        <Fa icon={faThumbsDown} size="sm" class="inline-block" /> ({Number(review?.votes?.down) +
                            optimisticThumbs.filter((thumb) => thumb.reviewId === review?.uuid && thumb.direction === 'down').length})
                    </button>
                </div>
                {#if review?.replies}
                    <div class="mt-4 rounded-xl bg-purple-300 p-4 text-gray-900 shadow-sm">
                        <h3 class="mb-2 font-semibold italic">Caktus Vape replied:</h3>
                        <p>{review.replies}</p>
                    </div>
                {/if}
            </div>
        {/each}

        <div class="grid grid-cols-3 items-center">
            <div class="text-left">
                {#if page > 1}
                    <button
                        class="my-2 mr-2 inline-block cursor-pointer rounded-full border-2 border-primary bg-primary px-4 py-1.5 text-sm text-color-text text-white"
                        on:click={prevPage}>Previous</button
                    >
                {/if}
            </div>
            <div class="text-center">
                Page {page}
            </div>
            <div class="text-right">
                {#if reviewData?.meta?.totalReviews > page * limit}
                    <button
                        class="my-2 mr-2 inline-block cursor-pointer rounded-full border-2 border-primary bg-primary px-4 py-1.5 text-sm text-color-text text-white"
                        on:click={nextPage}>Next</button
                    >
                {/if}
            </div>
        </div>
    {/if}
{:catch error}
    <p style="color: red">{error}</p>
{/await}

<Modal name="JudgeMeReviewThanks" bind:modalOpen={openThanksMessage}>
    <div class="m-auto flex min-h-72 max-w-xs flex-col items-center justify-evenly">
        <h3 class="text-lg font-semibold dark:text-purple-300">We've got your review!</h3>
        <p class="text-center">Thanks for taking the time to leave your review, we always love hearing your feedback!</p>
        <a
            class="btn inline-block w-full bg-primary bg-none text-base capitalize text-white disabled:bg-gray-400 disabled:text-gray-600 lg:w-auto"
            href={$pageData?.url?.pathname}
            title="Close Modal">Close Message</a
        >
    </div>
</Modal>
